import { createGlobalStore } from 'hox';
import { useCallback, useState } from 'react';
import { MenuOption } from 'src/types';
import { updateOrgId } from 'src/utils/request';

import {
  char2entity,
  getCurrentSelectedOrgId,
  getDefaultSingleOrgId,
} from '../utils/common';
import i18next from 'i18next';

type OrgID = string;
type OrgName = string;

function useOrg() {
  const [orgId, setOrgId] = useState<string>('all');
  const [defaultSingleOrgId, setDefaultSingleOrgId] = useState<string>('');
  const [orgs, setOrgs] = useState<MenuOption[] | null>(null);
  const [orgsMap, setOrgsMap] = useState<Map<OrgID, OrgName>>(new Map());
  const switchOrgId = (id: string) => {
    localStorage.setItem('casCurSelectedOrgId', id);
    updateOrgId(id);
    setOrgId(id);
    if (id !== 'all') {
      setDefaultSingleOrgId(id);
    }
  };

  const setOrgsAndOrgsMap = useCallback((orgItems: MenuOption[]) => {
    const currentOgrId = getCurrentSelectedOrgId(orgItems);
    if (currentOgrId) {
      switchOrgId(currentOgrId);
    }
    const defaultSingleOrgId = getDefaultSingleOrgId(orgItems);
    if (defaultSingleOrgId) {
      setDefaultSingleOrgId(defaultSingleOrgId);
    }

    orgItems.forEach((org) => {
      org.value = char2entity(org.value);
    });
    setOrgs(orgItems);
    const _orgMap: Map<OrgID, OrgName> = new Map();
    orgItems.forEach((org) => {
      _orgMap.set(org.key, org.value);
    });
    setOrgsMap(_orgMap);
  }, []);

  const getOrgName = useCallback(
    (orgId: 'all' | (string & {})) => {
      if (orgId === 'all') {
        return i18next.t('common.label.allOrganizations');
      }
      return (
        orgsMap.get(orgId) || i18next.t('common.hints.deleted') + ': ' + orgId
      );
    },
    [orgsMap]
  );

  return {
    /** current selected org id */
    orgId,
    switchOrgId,
    defaultSingleOrgId,
    orgs,
    orgsMap,
    setOrgs: setOrgsAndOrgsMap,
    getOrgName,
  };
}

export const [useOrgStore, getOrgStore] = createGlobalStore(useOrg);
